import React from 'react';
import * as Icons from '@mui/icons-material';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from '../../../components/Link';
import { DataTable } from '../../../components/DataTable';
import * as Entities from '../../../constants/Entities';
import { entityLocation } from '../../../utils/entity';
import { TooltipIcon } from '../../../components/TooltipIcon';

interface DeviceModel {
  id: string;
  name?: string | null;
  isGlobal?: boolean | null;
  deviceModelCapabilities?: Array<unknown> | null;
  devices?: Array<unknown> | null;
}

interface DeviceModelsListProps {
  deviceModels: DeviceModel[];
  onAdd?: () => void;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  page: number;
  pageSize: number;
  count: number;
  onSearchSubmit: (query: string) => void;
  searchValue: string;
  loading: boolean;
}

export const DeviceModelsList: React.FC<DeviceModelsListProps> = ({
  deviceModels,
  onAdd,
  onChangePageSize,
  onChangePage,
  page,
  pageSize,
  count,
  onSearchSubmit,
  searchValue,
  loading,
}) => {
  const { t } = useTranslation(['deviceModels', 'general']);

  const headers = [
    {
      key: 'name',
      value: t('deviceModels:list.headers.name'),
    },
    {
      key: 'capabilityCount',
      value: t('deviceModels:list.headers.capabilityCount'),
    },
    {
      key: 'deviceCount',
      value: t('deviceModels:list.headers.deviceCount'),
    },
    {
      key: 'deviceModelType',
      value: (
        <TooltipIcon
          text={t('deviceModels:list.headers.deviceModelType')}
          tootltipText={t('deviceModels:list.deviceModelTypeTooltip')}
        />
      ),
    },
    {
      key: 'actions',
      value: '',
    },
  ];

  const rows = deviceModels.map((device) => ({
    key: `${device.id}-row`,
    cells: [
      {
        key: `${device.id}-cell-name`,
        element: (
          <Link to={entityLocation(Entities.DEVICEMODEL, device.id)}>
            {device.name || device.id}
          </Link>
        ),
      },
      {
        key: `${device.id}-cell-capabilityCount`,
        element: device.deviceModelCapabilities?.length ?? 0,
      },
      {
        key: `${device.id}-cell-deviceCount`,
        element: device.devices?.length ?? 0,
      },
      {
        key: `${device.id}-cell-deviceModelType`,
        element: device.isGlobal
          ? t('general:entityType.global')
          : t('general:entityType.local'),
      },
      {
        key: `${device.id}-cell-actions`,
        element: null,
      },
    ],
  }));

  return (
    <DataTable
      actions={
        onAdd && (
          <Button
            color="secondary"
            variant="outlined"
            size="large"
            startIcon={<Icons.Add />}
            onClick={onAdd}
          >
            {t('deviceModels:list.actions.addButton')}
          </Button>
        )
      }
      page={page}
      pageSize={pageSize}
      count={count}
      onChangePage={onChangePage}
      onChangePageSize={onChangePageSize}
      headers={headers}
      rows={rows}
      showPagination
      showSearch
      onSearchSubmit={onSearchSubmit}
      searchValue={searchValue}
      loading={loading}
    />
  );
};
